import { Controller } from "@hotwired/stimulus"

// Portfolio dashboard thresholds settings modal.
//
// - Profile buttons (Conservative / Balanced / Aggressive) pre-fill the 6 numeric fields.
// - Editing any field marks the profile as "Custom".
// - Live preview renders RAG sample values using the current inputs.
// - Save POSTs to companies#update_portfolio_settings and reloads the dashboard turbo frame.
export default class extends Controller {
  static targets = [
    "profileButton", "profileNote", "errors", "preview", "submitBtn",
    "budgetAmber", "budgetRed",
    "scheduleAmber", "scheduleRed",
    "itemsAmber", "itemsRed"
  ]
  static values = {
    url: String,
    profiles: Object,
    defaults: Object
  }

  connect() {
    this._renderPreview()
  }

  pickProfile(event) {
    const slug = event.currentTarget.dataset.profile
    const preset = this.profilesValue[slug]
    if (!preset) return

    this.budgetAmberTarget.value = preset.budget_amber_pct
    this.budgetRedTarget.value = preset.budget_red_pct
    this.scheduleAmberTarget.value = preset.schedule_amber_days
    this.scheduleRedTarget.value = preset.schedule_red_days
    this.itemsAmberTarget.value = preset.items_amber_count
    this.itemsRedTarget.value = preset.items_red_count
    this._selectedProfile = slug
    this._highlightProfile(slug)
    this._renderPreview()
  }

  onFieldEdit() {
    this._selectedProfile = "custom"
    this._highlightProfile(null)
    this.profileNoteTarget.textContent = "Custom — fine-tuned from a preset."
    this._renderPreview()
  }

  resetDefaults() {
    const d = this.defaultsValue
    this.budgetAmberTarget.value = d.budget_amber_pct
    this.budgetRedTarget.value = d.budget_red_pct
    this.scheduleAmberTarget.value = d.schedule_amber_days
    this.scheduleRedTarget.value = d.schedule_red_days
    this.itemsAmberTarget.value = d.items_amber_count
    this.itemsRedTarget.value = d.items_red_count
    this._selectedProfile = d.profile || "balanced"
    this._highlightProfile(this._selectedProfile)
    this.profileNoteTarget.textContent = "Reset to balanced defaults. Save to apply."
    this._renderPreview()
  }

  async save() {
    this.errorsTarget.classList.add("hidden")
    this._setSubmitting(true)

    const body = new FormData()
    body.append("profile", this._selectedProfile || "custom")
    body.append("budget_amber_pct", this.budgetAmberTarget.value)
    body.append("budget_red_pct", this.budgetRedTarget.value)
    body.append("schedule_amber_days", this.scheduleAmberTarget.value)
    body.append("schedule_red_days", this.scheduleRedTarget.value)
    body.append("items_amber_count", this.itemsAmberTarget.value)
    body.append("items_red_count", this.itemsRedTarget.value)

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
          Accept: "application/json"
        },
        body
      })
      const data = await response.json()
      if (!response.ok || !data.success) {
        this._showError((data.errors || ["Save failed"]).join(", "))
        return
      }
      this._showToast("Thresholds updated", "notice")
      this.dispatch("close")
      this._reloadDashboard()
    } catch (err) {
      this._showError(err.message || "Network error")
    } finally {
      this._setSubmitting(false)
    }
  }

  _highlightProfile(slug) {
    this.profileButtonTargets.forEach(btn => {
      const match = btn.dataset.profile === slug
      btn.classList.toggle("border-brand", match)
      btn.classList.toggle("bg-brand/5", match)
      btn.classList.toggle("border-gray-200", !match)
    })
  }

  _renderPreview() {
    const bA = parseFloat(this.budgetAmberTarget.value) || 0
    const bR = parseFloat(this.budgetRedTarget.value) || 0
    const sA = parseInt(this.scheduleAmberTarget.value, 10) || 0
    const sR = parseInt(this.scheduleRedTarget.value, 10) || 0
    const iA = parseInt(this.itemsAmberTarget.value, 10) || 0
    const iR = parseInt(this.itemsRedTarget.value, 10) || 0

    this._setPreviewText("budget-green", `< ${bA}% over`)
    this._setPreviewText("budget-amber", `${bA}–${Math.max(bR - 0.1, bA).toFixed(1)}% over`)
    this._setPreviewText("budget-red", `≥ ${bR}% over`)

    this._setPreviewText("schedule-green", `< ${sA} days late`)
    this._setPreviewText("schedule-amber", `${sA}–${Math.max(sR - 1, sA)} days late`)
    this._setPreviewText("schedule-red", `≥ ${sR} days late`)

    this._setPreviewText("items-green", `< ${iA} overdue`)
    this._setPreviewText("items-amber", `${iA}–${Math.max(iR - 1, iA)} overdue`)
    this._setPreviewText("items-red", `≥ ${iR} overdue`)
  }

  _setPreviewText(slot, text) {
    const el = this.previewTarget.querySelector(`[data-preview="${slot}"]`)
    if (el) el.textContent = text
  }

  _setSubmitting(isSubmitting) {
    this.submitBtnTarget.disabled = isSubmitting
    this.submitBtnTarget.textContent = isSubmitting ? "Saving…" : "Save"
  }

  _showError(message) {
    this.errorsTarget.textContent = message
    this.errorsTarget.classList.remove("hidden")
  }

  _showToast(message, type = "notice") {
    const toast = document.createElement("div")
    toast.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.textContent = message
    document.body.prepend(toast)
  }

  _reloadDashboard() {
    const frame = document.getElementById("portfolio-dashboard-content")
    if (!frame) return
    // The frame is populated inline on initial render (no src attribute),
    // so frame.reload() is a no-op. Setting .src triggers Turbo to fetch
    // the current URL and swap in the matching frame from the response.
    frame.src = window.location.href
  }
}
